import { useMemo } from 'react';
import { BILLING_CYCLE_LABEL, type PlanItem } from './types';
import { fmtPrice } from './utils';

interface Props {
  plans: PlanItem[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}

const monthsOf = (p: PlanItem) => p.durationMonths;

/**
 * 套餐选择网格：展示价格、时长、计费周期与「每月折算」，并自动标记性价比最高的套餐。
 * 由结算弹窗复用；纯受控组件，不持有业务状态。
 */
export function PlanGrid({ plans, selectedIndex, onSelect }: Props) {
  // 计算每月均价最低的套餐作为「最划算」推荐（仅在有多个套餐时展示）
  const bestIdx = useMemo(() => {
    if (plans.length < 2) return -1;
    let best = -1;
    let bestPer = Infinity;
    plans.forEach((p, i) => {
      const per = Number(p.price) / Math.max(1, monthsOf(p));
      if (per < bestPer) {
        bestPer = per;
        best = i;
      }
    });
    return best;
  }, [plans]);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
      {plans.map((p, idx) => {
        const months = monthsOf(p);
        const perMonth = Number(p.price) / Math.max(1, months);
        const selected = selectedIndex === idx;
        return (
          <button
            key={p.planCode ?? idx}
            type="button"
            onClick={() => onSelect(idx)}
            className={`relative flex flex-col items-center gap-1 px-2 py-3.5 rounded-xl border-2 cursor-pointer transition-all ${
              selected ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'
            }`}
          >
            {idx === bestIdx && (
              <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] px-2 py-0.5 bg-amber-500 text-white rounded-full font-medium whitespace-nowrap">
                最划算
              </span>
            )}
            <span className="text-xs text-gray-500 mt-1">{p.name}</span>
            <span className={`text-xl font-bold ${selected ? 'text-amber-600' : 'text-gray-800'}`}>¥{fmtPrice(p.price)}</span>
            <span className="text-xs text-gray-400">
              / {months} 个月{p.billingCycle ? ` · ${BILLING_CYCLE_LABEL[p.billingCycle] ?? ''}` : ''}
            </span>
            {months > 1 && (
              <span className="text-[11px] text-amber-500 font-medium">约 ¥{fmtPrice(Math.round(perMonth * 100) / 100)}/月</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
